import { useEffect, useRef, useState } from 'react';

export interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export type TargetStatus = 'searching' | 'found' | 'missing';

interface UseWalkthroughTargetOptions {
  /** Seletor CSS do alvo. `null` = passo sem alvo (modal centralizado). */
  selector: string | null;
  /** Quanto tempo esperar o elemento montar antes de desistir. */
  waitForSelectorMs?: number;
  /** Rolar o alvo para o centro da viewport ao encontrá-lo. */
  scrollIntoView?: boolean;
  /** Pausa a medição (ex.: durante navegação entre rotas). */
  paused?: boolean;
}

const POLL_INTERVAL_MS = 120;
const DEFAULT_WAIT_MS = 4000;

function readRect(element: Element): TargetRect {
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
}

function sameRect(a: TargetRect | null, b: TargetRect | null): boolean {
  if (!a || !b) return a === b;
  return (
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  );
}

/**
 * Localiza e mede o elemento alvo de um passo do tour.
 *
 * O elemento pode ainda não existir (rota trocando, lista carregando), então
 * o hook faz polling até `waitForSelectorMs`. Se não aparecer, devolve
 * `status: 'missing'` — o overlay então degrada o passo para modal centralizado
 * em vez de travar o tour.
 *
 * Depois de encontrado, acompanha scroll, resize e mudanças de layout.
 */
export function useWalkthroughTarget({
  selector,
  waitForSelectorMs = DEFAULT_WAIT_MS,
  scrollIntoView = true,
  paused = false,
}: UseWalkthroughTargetOptions) {
  const [rect, setRect] = useState<TargetRect | null>(null);
  const [status, setStatus] = useState<TargetStatus>(
    selector ? 'searching' : 'missing',
  );
  const rectRef = useRef<TargetRect | null>(null);

  useEffect(() => {
    rectRef.current = rect;
  }, [rect]);

  // O alvo mudou: zera a medição imediatamente, mesmo em pausa, para o
  // spotlight nunca destacar o elemento do passo anterior.
  useEffect(() => {
    setRect(null);
    rectRef.current = null;
    setStatus(selector ? 'searching' : 'missing');
  }, [selector]);

  useEffect(() => {
    if (paused) return;

    // Passo sem alvo: modal centralizado
    if (!selector) return;

    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let rafId: number | undefined;
    let resizeObserver: ResizeObserver | undefined;
    let element: Element | null = null;
    const startedAt = Date.now();

    const measure = () => {
      if (cancelled || !element) return;
      // Elemento saiu do DOM (troca de rota, re-render): volta a procurar
      if (!element.isConnected) {
        element = null;
        setStatus('searching');
        poll();
        return;
      }
      const next = readRect(element);
      if (!sameRect(rectRef.current, next)) {
        rectRef.current = next;
        setRect(next);
      }
    };

    const scheduleMeasure = () => {
      if (rafId !== undefined) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    };

    const attach = (found: Element) => {
      element = found;

      if (scrollIntoView) {
        found.scrollIntoView({ block: 'center', inline: 'nearest' });
      }

      // Uma volta no event loop para o scroll assentar antes de medir
      rafId = requestAnimationFrame(() => {
        if (cancelled) return;
        const initial = readRect(found);
        rectRef.current = initial;
        setRect(initial);
        setStatus('found');
      });

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(scheduleMeasure);
        resizeObserver.observe(found);
      }
      window.addEventListener('scroll', scheduleMeasure, true);
      window.addEventListener('resize', scheduleMeasure);
    };

    const poll = () => {
      if (cancelled) return;

      let found: Element | null = null;
      try {
        found = document.querySelector(selector);
      } catch {
        // Seletor inválido digitado no admin: trata como ausente
        setStatus('missing');
        return;
      }

      if (found) {
        attach(found);
        return;
      }

      if (Date.now() - startedAt >= waitForSelectorMs) {
        setStatus('missing');
        return;
      }

      pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      if (rafId !== undefined) cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
      window.removeEventListener('scroll', scheduleMeasure, true);
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [selector, waitForSelectorMs, scrollIntoView, paused]);

  return { rect, status };
}
